import { inject, Injectable, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ManagementContentFacade } from '@thiememeulenhoff/ng-store';
import { ResourceType } from '@thiememeulenhoff/types';
import { map } from 'rxjs';
import { mapClustersWithArrangementsByMethodSlug } from '../_utils/map-clusters-with-arrangements-by-method-slug.util';

@Injectable({ providedIn: 'root' })
export class ManagementContentService {
  readonly #injector = inject(Injector);

  readonly #managementContentFacade = inject(ManagementContentFacade);

  enableLocalParsing() {
    this.#managementContentFacade.enableLocalParsing();
  }

  arrangement$(code: string) {
    return toObservable(this.#managementContentFacade.arrangement(code), {
      injector: this.#injector,
    });
  }

  arrangementResourceByCode$(arrangementCode: string) {
    return toObservable(
      this.#managementContentFacade.arrangementResourceByCode(arrangementCode),
      {
        injector: this.#injector,
      },
    );
  }

  resourceById$(contentIdentifier: string) {
    return toObservable(
      this.#managementContentFacade.resourceById(contentIdentifier),
      {
        injector: this.#injector,
      },
    );
  }

  contentById$(contentIdentifier: string) {
    return toObservable(
      this.#managementContentFacade.contentById(contentIdentifier),
      {
        injector: this.#injector,
      },
    );
  }

  clustersWithArrangementsByMethodSlug$(methodSlug: string) {
    return toObservable(this.#managementContentFacade.arrangementsList, {
      injector: this.#injector,
    }).pipe(
      map((arrangements) =>
        mapClustersWithArrangementsByMethodSlug(arrangements, methodSlug),
      ),
    );
  }

  fetchArrangement(code: string) {
    return this.#managementContentFacade.fetchArrangement(code);
  }

  fetchArrangementResource(code: string) {
    return this.#managementContentFacade.fetchArrangementResource(code);
  }

  fetchArrangementResourceWithSources(code: string) {
    return this.#managementContentFacade.fetchArrangementResourceWithSources(
      code,
    );
  }

  fetchResources(
    arrangementCode: string,
    contentIdentifier: string,
    disabled = true,
    types?: { include?: Array<ResourceType>; exclude?: Array<ResourceType> },
  ) {
    return this.#managementContentFacade.fetchResources(
      arrangementCode,
      contentIdentifier,
      disabled,
      types,
    );
  }

  fetchContent(
    arrangementCode: string,
    contentIdentifier: string,
    includeSourceMaterial = false,
  ) {
    return this.#managementContentFacade.fetchContent(
      arrangementCode,
      contentIdentifier,
      includeSourceMaterial,
    );
  }
}
